import origGql from 'graphql-tag';
import type { ApolloClient } from '@apollo/client';
import type { ApolloBase } from 'apollo-angular';
import { Kind, type DocumentNode, type FragmentDefinitionNode } from 'graphql';
import type { Observable } from 'rxjs';


export function gql(literals: any, ...placeholders: any[]) {
  return () => {
    if (placeholders) {
      placeholders = placeholders.map(placeholder => (typeof placeholder === 'function') ? placeholder() : placeholder);
    }

    return origGql(literals, ...placeholders) as DocumentNode;
  };
}

export function getFragmentName(fragment: () => DocumentNode) {
  const ast = fragment();

  const fragmentDefinitionNode = ast.definitions.find(node => node.kind === Kind.FRAGMENT_DEFINITION) as FragmentDefinitionNode;

  return fragmentDefinitionNode ? fragmentDefinitionNode.name.value : null;
}

export function patchClient(client: ApolloBase) {
  // Patch Apollo client instance to allow passing lazily parsed gql queries
  if (!(client as any).patched) {
    const mutate = client.mutate;
    const query = client.query;

    client.mutate = (...options: any[]) => {
      if (typeof options[0].mutation === 'function') {
        options[0].mutation = options[0].mutation();
      }

      return mutate.apply(client, options);
    };

    client.query = (...options: any[]) => {
      if (typeof options[0].query === 'function') {
        options[0].query = options[0].query();
      }

      return query.apply(client, options);
    };

    (client as any).patched = true;
  }

  return client as ApiPatchedClient;
}

interface ApiMutationOptions<T, V> extends Pick<ApolloClient.MutateOptions<T, V>, 'context' | 'fetchPolicy' | 'variables'> {
  mutation: any; // () => DocumentNode
}

interface ApiQueryOptions<V> extends ApolloClient.QueryOptions<V> {
  query: any; // () => DocumentNode
}

export interface ApiPatchedClient extends ApolloBase {
  mutate<T, V = { [key: string]: any; }>(options: ApiMutationOptions<T, V>): Observable<ApolloClient.MutateResult<T>>;
  query<T, V = { [key: string]: any; }>(options: ApiQueryOptions<V>): Observable<ApolloClient.QueryResult<T>>;
}

export function normalizeGqlParameters(parameters: any[][]) {
  return parameters
    .filter(Boolean)
    .map(([name, value]) =>
      [name, value ? typeof value === 'string' ? String.raw`"${value}"` : value : '']
        .filter(Boolean)
        .join(': ')
    )
    .join(', ');
}

export interface GqlVariable {
  name: string;
  type: string;
  value: any;
}

export function getGqlVariablesDef(variables: GqlVariable[]) {
  return variables.map(variable => `,$${variable.name}: ${variable.type}`).join('');
}

export function getGqlVariablesParams(variables: GqlVariable[]) {
  return variables.map(variable => `,${variable.name}: $${variable.name}`).join('');
}
